import { array, either, record } from "fp-ts";
import * as rubrieken from "./store/rubriekenReducer";
import { pipe } from "fp-ts/lib/function";
import * as cities from "./store/citiesReducer";
import * as gemeenschappelijkeAangifte from "./derived/gemeenschappelijkeAangifte";
import { rubriekenFp } from "./rubriek";
import { last } from "fp-ts/lib/Semigroup";
import { Value } from "./types";

export interface Aangifte {
  rubrieken: rubrieken.RubriekReducer;
  city: cities.CityReducer;
  isGemeenschappelijkeAangifte: boolean;
}

export interface Seed {
  postalcode?: string;
  rubrieken?: rubrieken.RubriekenSeed;
}

export type Action =
  | rubrieken.UpdateRubriekValueAction
  | cities.UpdatePostalcode
  | { type: "INIT" }
  | { type: "RESET" };

export type Dispatch = (a: Action) => either.Either<DomainError, Aangifte>;

export type DomainError = (
  | rubrieken.InvalidRubriekCode
  | rubrieken.InvalidValueType
  | gemeenschappelijkeAangifte.WARNING_PARTNER_CODES_FILLED_IN_FOR_SINGLE_AANGIFTE
) & { action: Action };

const init = (seed?: Seed): Aangifte => {
  const initializedRubrieken = rubrieken.init(seed);
  return {
    rubrieken: initializedRubrieken,
    city: cities.init(seed),
    isGemeenschappelijkeAangifte: gemeenschappelijkeAangifte.init(initializedRubrieken),
  };
};

export const initializeAangifte = (onUpdate: (aangifte: Aangifte) => void, seed?: Seed) => {
  let aangifte: Aangifte = init(seed);

  const dispatch: Dispatch = (action) => {
    const update = pipe(
      either.Do,
      either.bindW("rubrieken", () => rubrieken.update(action)(aangifte.rubrieken)),
      either.bindW("city", () => pipe(aangifte.city, cities.update(action), either.of)),
      either.bindW("isGemeenschappelijkeAangifte", ({ rubrieken }) =>
        pipe(rubrieken, gemeenschappelijkeAangifte.derive(aangifte.isGemeenschappelijkeAangifte))
      ),
      either.mapLeft((a) => ({ ...a, action }))
    );

    if (either.isRight(update)) {
      aangifte = update.right;
      onUpdate(aangifte);
    }

    return update;
  };

  return [aangifte, dispatch] as const;
};

export const aangifteToSeed = (aangifte: Aangifte): Seed => {
  return {
    postalcode: aangifte.city?.postalcode,
    rubrieken: pipe(
      aangifte.rubrieken,
      rubriekenFp.filter((r) => !!r.value),
      rubriekenFp.map((r) => r.value as Value),
      rubriekenFp.toArray,
      record.fromFoldable(last<Value>(), array.Foldable)
    ),
  };
};
